import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Car, Mail, Lock, User, Phone, Loader2, LogIn } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const isWorkerLogin = location.pathname === '/worker/login';
  const isAdminLogin = location.pathname === '/admin/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, rememberMe);
      
      if (isWorkerLogin && user.role !== 'WORKER' && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
        toast.error('No tienes permisos de trabajador');
        return;
      }
      
      if (isAdminLogin && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
        toast.error('No tienes permisos de administrador');
        return;
      }

      toast.success(`¡Bienvenido, ${user.name}!`);
      
      if (isWorkerLogin) {
        navigate('/worker');
      } else if (isAdminLogin) {
        navigate('/admin/reservas');
      } else {
        navigate('/account');
      }
    } catch (error) {
      toast.error(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const title = isWorkerLogin 
    ? 'Panel Trabajador' 
    : isAdminLogin 
      ? 'Panel Admin' 
      : t('auth.login');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="logo-link">
          <Logo className="h-28" />
        </Link>

        {/* Login Card */}
        <div className="card-dark p-8">
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-6">
            {title}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className="input-gold pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-gold pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Remember Me Checkbox - Only for Admin/Worker */}
            {(isWorkerLogin || isAdminLogin) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  data-testid="remember-me-checkbox"
                />
                <label
                  htmlFor="remember-me"
                  className="text-sm text-zinc-400 cursor-pointer select-none"
                >
                  {i18n.language === 'es' ? 'Mantener sesión iniciada' : 'Keep me logged in'}
                </label>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.login')
              )}
            </Button>
          </form>

          {!isWorkerLogin && !isAdminLogin && (
            <div className="mt-6 text-center space-y-3">
              <p className="text-zinc-400 text-sm">
                <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">
                  {i18n.language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </Link>
              </p>
              <p className="text-zinc-400 text-sm">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                  {t('auth.register')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
